/**
 * AvatarDisplay Component
 * Displays an avatar with support for different moods and accessory layers.
 * CDN: sad.png / angry.png / idle.png / happy.png; on load error tries fallback (sad->angry, happy->idle).
 */

import React, { useState, useCallback, useEffect } from 'react';
import { View, Text } from 'react-native';
import { Image } from 'expo-image';
import type { ViewStyle, ImageStyle, StyleProp } from 'react-native';
import {
  normalizeDisplayAvatarId,
  type AvatarMood,
  type AvatarHomeAway,
  type DisplayAvatarId,
} from '../../../src/avatars/types/avatar.types';
import { getAvatarLoadAttempts } from '../../../src/avatars/helpers/getAvatarAsset';
import {
  AVATAR_LOAD_RETRY_DELAY_MS,
  INITIAL_AVATAR_LOAD_STATE,
  advanceAvatarLoadOnError,
  retryAvatarLoad,
} from '../../../src/avatars/helpers/avatarLoadRetry.helper';
import { getAvatarSizeStyles } from '../../../src/avatars/helpers/getAvatarSizeStyles';
import { LayeredAvatarView } from '../LayeredAvatarLab/LayeredAvatarView';
import { canRenderLayeredAvatar } from '../LayeredAvatarLab/characterCustomizer.helper';
import type { EquippedMap } from '../LayeredAvatarLab/characterCustomizer.types';
import { avatarDisplayStyles as styles } from './AvatarDisplay.styles';
import { WEB_IMAGE_ALT_AVATAR_DEFAULT } from '../../../src/shared/helpers/imageWebAlt.helper';

export interface AvatarDisplayProps {
  /** Avatar identifier (user-selectable or display-only e.g. monkey). Optional when avatarConfig is set. */
  avatarId?: DisplayAvatarId;
  /**
   * Custom layered look. When non-null it overrides avatarId/mood/variant and renders the
   * layered idle composite (the customizer has idle only — non-idle moods fall back to idle).
   */
  avatarConfig?: EquippedMap | null;
  /** Mood state (default: 'idle'). Ignored when variant is home/away. */
  mood?: AvatarMood;
  /** Home/away for success screen: home = challenger, away = opponent. When set, overrides mood. */
  variant?: AvatarHomeAway;
  /**
   * Pixel size of the avatar (width = height). Prefer this over sizing via `style` —
   * it drives the 150×150 CDN variant auto-detection (size ≤ 150 → small image).
   * If you size via `style` instead, pass `small={true}` explicitly for small renders.
   */
  size?: number;
  /** Force 150×150 CDN variant. Auto-enabled when size ≤ 150 via the `size` prop. */
  small?: boolean;
  /** When true, applies circular mask to the avatar container. */
  rounded?: boolean;
  /** Optional custom styling */
  style?: ViewStyle;
  /** Children for accessory layers (positioned absolutely) */
  children?: React.ReactNode;
  /** Test ID for testing */
  testID?: string;
  /** Web: HTML img alt (Lighthouse / accessibility). */
  imageAlt?: string;
}

/**
 * AvatarDisplay Component
 * Renders an avatar image with support for moods and accessory overlays.
 * On primary image load error, tries fallback (losing: angry; winning: idle).
 */
export const AvatarDisplay: React.FC<AvatarDisplayProps> = ({
  avatarId,
  avatarConfig,
  mood = 'idle',
  variant,
  size,
  small,
  rounded = true,
  style,
  children,
  testID,
  imageAlt = WEB_IMAGE_ALT_AVATAR_DEFAULT,
}) => {
  const resolvedAvatarId = normalizeDisplayAvatarId(avatarId);
  const isSmall = small ?? (size != null && size <= 150);

  const loadAttempts = React.useMemo(
    () => getAvatarLoadAttempts(resolvedAvatarId, mood, variant, isSmall),
    [resolvedAvatarId, mood, variant, isSmall],
  );

  const [loadState, setLoadState] = useState(INITIAL_AVATAR_LOAD_STATE);

  useEffect(() => {
    setLoadState(INITIAL_AVATAR_LOAD_STATE);
  }, [resolvedAvatarId, mood, variant, isSmall]);

  // Every URL failed (usually a flaky connection): try the chain once more after a pause.
  useEffect(() => {
    if (loadState.phase !== 'waiting-retry') return undefined;
    const timer = setTimeout(() => setLoadState(retryAvatarLoad), AVATAR_LOAD_RETRY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [loadState.phase]);

  // While waiting or after giving up, show the placeholder rather than a failed (blank) image.
  const source =
    loadState.phase === 'loading' ? (loadAttempts[loadState.attemptIndex] ?? null) : null;

  const onError = useCallback(() => {
    setLoadState((state) => advanceAvatarLoadOnError(state, loadAttempts.length));
  }, [loadAttempts.length]);

  const sizeStyles = getAvatarSizeStyles(size, rounded);

  // Custom layered look wins over the ready avatar; idle only (mood/variant ignored).
  // Guard: only render the layered look if this client recognizes it (known base). An old app
  // that doesn't have a newly-added base falls through to the ready avatar instead of blanking.
  if (avatarConfig && canRenderLayeredAvatar(avatarConfig)) {
    return (
      <View
        style={[styles.container, sizeStyles?.container, style]}
        testID={testID || 'avatar-display-custom'}
      >
        <LayeredAvatarView equipped={avatarConfig} size={size ?? 96} rounded={rounded} />
        {children && <View style={styles.childrenContainer}>{children}</View>}
      </View>
    );
  }

  return (
    <View
      style={[styles.container, sizeStyles?.container, style]}
      testID={testID || `avatar-display-${resolvedAvatarId}`}
    >
      {source ? (
        <Image
          key={`${loadState.round}-${loadState.attemptIndex}`}
          source={source}
          style={[styles.image, sizeStyles?.image] as StyleProp<ImageStyle>}
          contentFit="contain"
          cachePolicy="memory-disk"
          onError={onError}
          alt={imageAlt}
        />
      ) : (
        <View style={[styles.ghostContainer, sizeStyles?.container]}>
          <Text style={[styles.ghostText, sizeStyles?.ghostText]}>?</Text>
        </View>
      )}
      {children && <View style={styles.childrenContainer}>{children}</View>}
    </View>
  );
};

